import { useRef } from 'react'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { PageSpinningLoader } from '@joyful/ui/core'
import {
  CurrentUserProtectedRoute,
  DemoModeRedactionProvider,
  getAppSwitchRoutePath,
  getNavbarAppHref,
  InitialPageLoadComplete,
  useActiveAccount,
  useCurrentUser,
  useHasFeatureFlag,
  useHasUserCapability,
  PageTitle,
} from '@joyful/ui/app'
import type { NavbarApp } from '@joyful/ui/app'
import { ActiveOrgProvider } from '@/components/layouts/ActiveOrgProvider'
import { OrgSelectionRedirect } from '@/features/navigation/components/OrgSelectionRedirect'
import { useNotifyExtensionActiveAccount } from '@/features/auth/hooks/useNotifyExtensionActiveAccount'
import { getPathOrganizationScope } from '@/features/auth/utils/getPathOrganizationScope'
import { ErrorRoute } from '@/routes/ErrorRoute'

interface AuthProviderLayoutProps {
  children: ReactNode
}

export function AuthProviderLayout({ children }: AuthProviderLayoutProps) {
  return (
    <CurrentUserProtectedRoute>
      <DemoModeRedactionProvider>
        <PageTitle />
        <ActiveOrgProvider>
          <AuthProviderLayoutInner>{children}</AuthProviderLayoutInner>
        </ActiveOrgProvider>
      </DemoModeRedactionProvider>
    </CurrentUserProtectedRoute>
  )
}

function AuthProviderLayoutInner({ children }: AuthProviderLayoutProps) {
  const currentUser = useCurrentUser()
  const activeAccount = useActiveAccount()
  const hasCapability = useHasUserCapability()
  const hasFeatureFlag = useHasFeatureFlag()
  const location = useLocation()
  useNotifyExtensionActiveAccount()

  if (!hasCapability('app.app.view')) {
    if (hasCapability('app.workspace.view')) {
      return (
        <ExternalAppRedirectRoute
          app="workspace"
          path={getAppSwitchRoutePath({
            app: 'workspace',
            hasCapability,
            hasFeatureFlag,
            hash: location.hash,
            orgSlug: activeAccount?.slug ?? null,
            pathname: location.pathname,
            search: location.search,
          })}
        />
      )
    }

    if (hasCapability('app.bpo.view')) {
      return (
        <ExternalAppRedirectRoute
          app="bpo"
          path={getAppSwitchRoutePath({
            app: 'bpo',
            hasCapability,
            hasFeatureFlag,
            hash: location.hash,
            orgSlug: activeAccount?.slug ?? null,
            pathname: location.pathname,
            search: location.search,
          })}
        />
      )
    }

    if (hasCapability('app.internal.view')) {
      return (
        <ExternalAppRedirectRoute
          app="internal"
          path={getAppSwitchRoutePath({
            app: 'internal',
            hasCapability,
            hasFeatureFlag,
            hash: location.hash,
            orgSlug: activeAccount?.slug ?? null,
            pathname: location.pathname,
            search: location.search,
          })}
        />
      )
    }

    return (
      <InitialPageLoadComplete>
        <ErrorRoute error={404} />
      </InitialPageLoadComplete>
    )
  }

  if (!currentUser.tos_accepted && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />
  }

  if (
    getPathOrganizationScope(location.pathname) !== null &&
    activeAccount === null
  ) {
    return (
      <OrgSelectionRedirect
        targetPath={`${location.pathname}${location.search}${location.hash}`}
      />
    )
  }

  return <InitialPageLoadComplete>{children}</InitialPageLoadComplete>
}

function ExternalAppRedirectRoute({
  app,
  path,
}: {
  app: NavbarApp
  path: string
}) {
  const href = getNavbarAppHref({ app, path })
  const hasStartedRedirectRef = useRef(false)

  return (
    <div
      ref={(element) => {
        if (element === null || hasStartedRedirectRef.current) {
          return
        }

        hasStartedRedirectRef.current = true
        window.location.replace(href)
      }}
    >
      <PageSpinningLoader />
    </div>
  )
}
